import BreadCrumb from "Common/BreadCrumb";
import Loader from "Common/Components/Loader";

import TableContainer from "Common/TableContainer";
import { Search } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "Common/SafeLink";
import { ToastContainer } from "react-toastify";
import { useTickets } from "./useTickets";

const filterDataBySearch = (data, search, keysToSearch, setFilteredData) => {
  const lowerCaseSearch = search.toLowerCase();
  const filtered = data.filter((item) =>
    keysToSearch.some((key) => {
      const value = item[key];
      if (value != null) {
        return value.toString().toLowerCase().includes(lowerCaseSearch);
      }
      return false;
    })
  );
  setFilteredData(filtered);
};
const Tickets = () => {
  const { tickets, isLoading} = useTickets();
  const [data, setData] = useState<any>([]);

  useEffect(() => {
    tickets && setData(tickets);
  }, [tickets]);

  const filterSearchData = (e: any) => {
    const search = e.target.value;
    const keysToSearch = ["game", "ticketNumber", "phoneNumber", "amount", "ticketType"];
    filterDataBySearch(tickets, search, keysToSearch, setData);
  };

  const columns = useMemo(
    () => [
      {
        header: "Ticket Number",
        accessorKey: "ticketNumber", // Updated to match the data key
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <>
            <Link
              to="#!"
              className="transition-all duration-150 ease-linear order_id text-custom-500 hover:text-custom-600"
            >
              {cell.getValue()}
            </Link>
          </>
        ),
      },

      {
        header: "Ticket Type", 
        accessorKey: "ticketType", 
        enableColumnFilter: false,
        cell: (cell: any) => cell.getValue() || 'N/A'
       
      },
      {
        header: "Amount",
        accessorKey: "ticketAmount", 
        enableColumnFilter: false,
        cell: (cell: any) => (
          <span className="text-sm text-slate-500 dark:text-zink-200 ">
            {" ₦  " + cell.getValue()}
          </span>
        ),
      },
      {
        header: "Number of Tickets",
        accessorKey: "quantity",
        enableColumnFilter: false,
      },
      {
        header: "User Phone Number",
        accessorKey: "phoneNumber", 
        enableColumnFilter: false,
      },
      {
        header: "Ticket Status",
        accessorKey: "ticketStatus",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <span
            className={`text-sm font-semibold py-1 px-2.5 rounded-full text-white ${
              cell.getValue() === "LOST"
                ? "bg-red-500"
                : cell.getValue() === "WON"
                ? "bg-green-500"
                : cell.getValue() === "NEW"
                ? "bg-yellow-500"
                : cell.getValue() === "USED"
                ? "bg-blue-500"
                : ""
            }`}
          >
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "Created Date",
        accessorKey: "createdAt", // Updated to match the data key
        enableColumnFilter: false,
        cell: (cell: any) => {
          const dateArray = cell.getValue();
          const date = new Date(
            dateArray[0], // year
            dateArray[1] - 1, // month (0-based index)
            dateArray[2], // day
            dateArray[3], // hour
            dateArray[4], // minute
            dateArray[5], // second
            dateArray[6] / 1000000 // milliseconds
          );

          return (
            <span className="text-sm text-slate-500 dark:text-zink-200">
              {date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>
          );
        },
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <BreadCrumb title="Tickets" pageTitle="Dashboard" />
      {isLoading && <Loader />}
      <ToastContainer closeButton={false} limit={1} />
      <div className="card" id="ordersTable">
        <div className="card-body">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
            <div className="lg:col-span-3">
              <div className="relative">
                <input
                  type="text"
                  className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  placeholder="Search by Game Name, Ticket ID, Phone Number, or Amount"
                  autoComplete="off"
                  onChange={(e) => filterSearchData(e)}
                />
                <Search className="inline-block size-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" />
              </div>
            </div>
            <div className="lg:col-span-2 lg:col-start-11">
              <div className="ltr:lg:text-right rtl:lg:text-left"></div>
            </div>
          </div>

          {data && data.length > 0 ? (
            <TableContainer
              isPagination={true}
              columns={columns || []}
              data={data || []}
              customPageSize={10}
              divclassName="mt-5 overflow-x-auto"
              tableclassName="w-full whitespace-nowrap"
              theadclassName="ltr:text-left rtl:text-right bg-slate-100 dark:bg-zink-600"
              thclassName="px-3.5 py-2.5 font-semibold text-slate-500 border-b border-slate-200 dark:border-zink-500 dark:text-zink-200"
              tdclassName="px-3.5 py-2.5 border-y border-slate-200 dark:border-zink-500"
              PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
            />
          ) : (
            <div className="noresult">
              <div className="py-6 text-center">
                <Search className="size-6 mx-auto text-sky-500 fill-sky-100 dark:sky-500/20" />
                <h5 className="mt-2 mb-1">Sorry! No Result Found</h5>
              </div>
            </div>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default Tickets;
